import { useCustomerIdToken } from '@hooks/customer/useCustomer';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import {
  GraphQLRequestWithTokenProps,
  requestWithTokenRequired,
} from '@repo/ui/src/utils/graphql-request';
import useSWRMutation from 'swr/mutation';
import { NoteProps, useRecipeNotes, NoteFragment } from './useRecipeNotes';

interface Props {
  recipeId: string;
}
interface Arg {
  comment: string;
}

interface Key extends GraphQLRequestWithTokenProps {
  recipeId: string;
}

const createNoteQuery = `
  ${NoteFragment}
  mutation CreateNote($input: CreateNoteInput!, $condition: ModelNoteConditionInput) {
    createNote(input: $input, condition: $condition) {
      ...Note
    }
  }
`;

const fetcher = async ({ recipeId, ...requestProps }: Key, { arg: { comment } }: { arg: Arg }) => {
  const data = await requestWithTokenRequired({
    ...requestProps,
    variables: {
      input: {
        recipeId,
        comment,
        privacy: 'private',
      },
    },
  });
  if (data?.errors) {
    throw new Error(data?.errors?.[0]?.message);
  }
  return data?.createNote as NoteProps;
};

export const useCreateNote = ({ recipeId }: Props) => {
  const { mutate } = useRecipeNotes({ recipeId });
  const { locale } = useCurrentUrl();
  const idToken = useCustomerIdToken();

  return useSWRMutation({ document: createNoteQuery, recipeId, locale, idToken }, fetcher, {
    onSuccess: (result) => {
      if (result) {
        mutate((notes) => [result, ...(notes ?? [])], { revalidate: false });
      }
    },
  });
};
